import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import ConfettiEffect from './effects/ConfettiEffect';

interface UnlockNotificationProps {
  actionName: string;
  actionIcon: string;
  description: string;
  onContinue: () => void;
}

const UnlockNotification: React.FC<UnlockNotificationProps> = ({
  actionName,
  actionIcon,
  description,
  onContinue,
}) => {
  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <ConfettiEffect />
      
      <motion.div
        className="bg-gradient-to-br from-yellow-900/95 to-orange-900/95 border-2 border-yellow-500/50 rounded-2xl p-8 max-w-lg w-full mx-4 shadow-2xl"
        initial={{ scale: 0.5, rotate: -10 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: 'spring', duration: 0.6, bounce: 0.5 }}
      >
        <motion.div
          className="text-center"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <motion.div
            className="inline-flex items-center gap-2 mb-4"
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 0.5, repeat: 2 }}
          >
            <Sparkles className="h-6 w-6 text-yellow-400" />
            <h2 className="text-2xl font-bold text-yellow-300">NEW ACTION UNLOCKED!</h2>
            <Sparkles className="h-6 w-6 text-yellow-400" />
          </motion.div>

          <motion.div
            className="text-7xl mb-4"
            animate={{ 
              rotate: [0, -10, 10, -10, 0],
              scale: [1, 1.2, 1]
            }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            {actionIcon}
          </motion.div>

          <h3 className="text-3xl font-bold text-white mb-3">{actionName}</h3>
          
          <p className="text-yellow-100 text-lg mb-6 px-4">
            {description}
          </p>

          <div className="bg-yellow-500/20 border border-yellow-500/40 rounded-lg p-4 mb-6">
            <p className="text-yellow-200 text-sm">
              🎊 This new action is now available in your action deck!
            </p>
          </div>

          <motion.button
            onClick={onContinue}
            className="w-full bg-gradient-to-r from-yellow-600 to-orange-600 text-white font-bold py-4 rounded-xl hover:from-yellow-700 hover:to-orange-700 transition-all shadow-lg"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Try It Now! →
          </motion.button>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default UnlockNotification;
